import apiClient from '../api/apiClient';
import type { AxiosError } from 'axios';
import {
  AssignmentContact,
  Incident,
  IncidentListResponse,
  CreateIncidentRequest,
  CreateIncidentResponse,
  CreateIncidentResponseData,
  DefaultIncidentName,
  IncidentSummary,
  IncidentSummaryResponse,
  ActivityEntry,
  IncidentActivityResponse,
  IncidentActivityItem,
  IncidentActivityPage,
  IncidentRosterPerson,
  IncidentRosterResponse,
  StatusBatchRequest,
  StatusBatchResponse,
  StatusBatchResponseData,
  StatusUpdateInput,
  AddUnlistedPersonRequest,
  AddUnlistedPersonResponse,
  UnaccountedResponse,
  CreateCheckpointRequest,
  CreateCheckpointResponse,
  CloseIncidentRequest,
  CloseIncidentResponse,
  UpdateIncidentDetailsRequest,
  UpdateIncidentDetailsResponse,
} from '../Types/Incident';
import type { IncidentAssignmentsResponse } from '../Types/IncidentAssignment';
import { normalizeApiPersonStatus, toApiPersonStatus } from '../Utils/headcountStatus';

import { IncidentType } from '../Types/enums';

/** Map frontend string IncidentType to the numeric code the POST API expects. */
const INCIDENT_TYPE_API_CODE: Record<string, number> = {
  [IncidentType.Drill]: 0,
  [IncidentType.Fire]: 1,
  [IncidentType.Weather]: 2,
  [IncidentType.Medical]: 3,
  [IncidentType.Security]: 4,
  [IncidentType.Hazmat]: 5,
};

/** Preserve unselected types and normalize supported numeric or string enum responses. */
const normalizeIncidentType = (raw: unknown): IncidentType | null => {
  if (raw == null || raw === '') return null;
  const value = typeof raw === 'string' ? raw.trim().toLowerCase() : raw;
  return Object.values(IncidentType).find((type) =>
    value === type || value === INCIDENT_TYPE_API_CODE[type] || value === String(INCIDENT_TYPE_API_CODE[type])) ?? null;
};

const incidentTypeApiCode = (type?: string | null): number | null => {
  if (type == null || type.trim() === '') return null;
  const normalized = normalizeIncidentType(type);
  if (normalized == null) throw new Error('Select a valid incident type.');
  return INCIDENT_TYPE_API_CODE[normalized];
};

/** Fetch a suggested name for this office's current local day. It does not reserve a number. */
export const getDefaultIncidentName = async (locationId: string): Promise<DefaultIncidentName> => {
  const response = await apiClient.get<{ data: DefaultIncidentName }>(
    `/api/incidents/default-name?locationId=${encodeURIComponent(locationId)}`,
  );
  const suggestion = response.data?.data;
  if (!suggestion || typeof suggestion.incidentName !== 'string' || !suggestion.incidentName.trim() ||
      !Number.isSafeInteger(suggestion.sequenceNumber) || suggestion.sequenceNumber < 1 ||
      typeof suggestion.generatedAtUtc !== 'string' || !Number.isFinite(Date.parse(suggestion.generatedAtUtc))) {
    throw new Error('The default incident name could not be loaded. Retry or enter a name.');
  }
  return suggestion;
};

/** Normalize numeric incident status (0=active, 1=closed) to string. */
const normalizeIncidentStatus = (raw: unknown): string => {
  if (typeof raw === 'number') return raw === 0 ? 'active' : 'closed';
  if (typeof raw === 'string') return raw;
  return 'active';
};

export const getIncidents = async (): Promise<IncidentListResponse> => {
  const response = await apiClient.get<IncidentListResponse>('/api/incidents');
  const body = response.data;
  // Normalize assignment arrays on each incident
  if (Array.isArray(body.data)) {
    body.data = body.data.map((inc) => ({
      ...inc,
      status: normalizeIncidentStatus(inc.status),
      incidentType: normalizeIncidentType(inc.incidentType),
      incidentCommanders: inc.incidentCommanders ?? [],
      headcounters: inc.headcounters ?? [],
      scopeCounts: (inc as unknown as Record<string, unknown>).scopeCounts as typeof inc.scopeCounts ?? undefined,
    }));
  }
  return body;
};

export const getIncidentDetail = async (incidentId: string): Promise<Incident> => {
  // Try dedicated detail endpoint first, fall back to finding in list
  try {
    const response = await apiClient.get<{ data: Incident } | Incident>(`/api/incidents/${encodeURIComponent(incidentId)}`);
    const body = response.data as Record<string, unknown>;
    const incident = (body.data as Incident) ?? (body as unknown as Incident);
    return { ...incident, status: normalizeIncidentStatus(incident.status), incidentType: normalizeIncidentType(incident.incidentType) };
  } catch {
    const list = await getIncidents();
    const found = list.data.find((i) => i.incidentId === incidentId);
    if (!found) throw new Error('Incident not found');
    return { ...found, status: normalizeIncidentStatus(found.status), incidentType: normalizeIncidentType(found.incidentType) };
  }
};

export const createIncident = async (request: CreateIncidentRequest): Promise<CreateIncidentResponseData> => {
  const payload = {
    ...request,
    incidentType: incidentTypeApiCode(request.incidentType),
  };
  const response = await apiClient.post<CreateIncidentResponse>('/api/incidents', payload).catch((error: unknown) => {
    if (error != null && typeof error === 'object') {
      const response = (error as AxiosError<{ error?: { code?: string } }>).response;
      const apiError = response?.data?.error;
      if (response?.status === 409 && apiError?.code === 'active_incident_exists') {
        throw new Error('An active incident already exists at this location. Open the existing incident or close it before starting another.');
      }
      if (response?.status === 400 && apiError?.code === 'invalid_location') {
        throw new Error('The selected location is unavailable. Refresh the locations and select an active office.');
      }
    }
    throw error;
  });
  const body = response.data;
  const data = body?.data;
  const incidentId = data?.incidentId ?? body?.incidentId;
  if (!incidentId) {
    throw new Error('No incidentId returned from the API.');
  }
  // Return full response data if available, otherwise construct minimal shape
  if (data && data.incidentId) {
    return {
      ...data,
      status: normalizeIncidentStatus(data.status),
      incidentType: normalizeIncidentType(data.incidentType),
    };
  }
  return {
    incidentId,
    incidentName: request.incidentName,
    incidentType: normalizeIncidentType(request.incidentType),
    location: request.location ?? null,
    status: 'active',
    startedAtUtc: new Date().toISOString(),
  };
};

const normalizeSummary = (raw: IncidentSummaryResponse): { summary: IncidentSummary; serverTimeUtc?: string } => {
  const inner = (raw.data ?? raw) as Record<string, unknown>;
  // API may nest counts under a `summary` sub-object
  const counts = (inner.summary as Record<string, unknown>) ?? inner;
  return {
    summary: {
      ...(inner as unknown as IncidentSummary),
      // Incident metadata — may live at inner or nested summary level
      // Use undefined (not 0) as fallback so consuming pages can fall through to incident detail
      incidentName: (inner.incidentName as string) ?? (counts.incidentName as string) ?? '',
      incidentType: Object.prototype.hasOwnProperty.call(inner, 'incidentType')
        ? normalizeIncidentType(inner.incidentType)
        : Object.prototype.hasOwnProperty.call(counts, 'incidentType') ? normalizeIncidentType(counts.incidentType) : undefined,
      location: (inner.location as string) ?? (inner.locationName as string) ?? (counts.location as string) ?? (counts.locationName as string) ?? '',
      startedAtUtc: (inner.startedAtUtc as string) ?? (counts.startedAtUtc as string) ?? '',
      closedAtUtc: (inner.closedAtUtc as string | null) ?? (counts.closedAtUtc as string | null) ?? null,
      status: normalizeIncidentStatus(inner.status ?? counts.status),
      // Counts
      total: (counts.total as number) ?? 0,
      accounted: (counts.accounted as number) ?? 0,
      notAccounted: (counts.notAccounted as number) ?? 0,
      notChecked: (counts.notChecked as number) ?? 0,
      tenants: (counts.tenants as number) ?? 0,
      percentComplete: (counts.percentComplete as number) ?? 0,
      unsyncedChanges: (inner.unsyncedChanges as number) ?? 0,
      accountabilityByView: (inner.accountabilityByView as IncidentSummary['accountabilityByView']) ?? [],
      headcounterActivity: (inner.headcounterActivity as IncidentSummary['headcounterActivity']) ?? [],
      incidentCommanders: (inner.incidentCommanders as AssignmentContact[]) ?? [],
      headcounters: (inner.headcounters as AssignmentContact[]) ?? [],
      assignedOffice: (counts.assignedOffice as number) ?? undefined,
      assignedOfficeNotBadgedIn: (counts.assignedOfficeNotBadgedIn as number) ?? undefined,
      scopeCounts: (counts.scopeCounts as IncidentSummary['scopeCounts']) ?? (inner.scopeCounts as IncidentSummary['scopeCounts']) ?? undefined,
      visitorCount: (counts.visitorCount as number) ?? (inner.visitorCount as number) ?? undefined,
    },
    serverTimeUtc: raw.meta?.serverTimeUtc,
  };
};

export const getIncidentSummary = async (incidentId: string): Promise<{ summary: IncidentSummary; serverTimeUtc?: string }> => {
  const response = await apiClient.get<IncidentSummaryResponse>(`/api/incidents/${encodeURIComponent(incidentId)}/summary`);
  return normalizeSummary(response.data);
};

export const getIncidentActivity = async (incidentId: string): Promise<{ activity: ActivityEntry[]; serverTimeUtc?: string }> => {
  const response = await apiClient.get<IncidentActivityResponse>(`/api/incidents/${encodeURIComponent(incidentId)}/activity?pageNumber=1&pageSize=50`);
  const body = response.data;
  const dataPayload = body.data as unknown as Record<string, unknown>;
  // API returns paginated { data: { items: [...] } } or flat { data: [...] }
  const rawItems: Record<string, unknown>[] = Array.isArray(dataPayload)
    ? dataPayload
    : Array.isArray(dataPayload?.items)
      ? dataPayload.items as Record<string, unknown>[]
      : [];
  // Map API fields to the ActivityEntry shape used by components
  const resolveName = (item: Record<string, unknown>): string => {
    const raw = (item.personDisplayName as string) ?? (item.actorDisplayName as string) ?? (item.personName as string) ?? '';
    // API sometimes returns generic placeholder names — fall back to updatedBy or a clean label
    if (!raw || raw === 'Unknown User' || raw === 'System') {
      return (item.actorDisplayName as string) && (item.actorDisplayName as string) !== 'Unknown User'
        ? (item.actorDisplayName as string)
        : (item.updatedBy as string) ?? 'Incident Commander';
    }
    return raw;
  };
  const activity: ActivityEntry[] = rawItems.map((item) => ({
    timestamp: (item.occurredAtUtc as string) ?? (item.timestamp as string) ?? '',
    personName: resolveName(item),
    action: (item.activityType as string) ?? (item.action as string) ?? '',
    context: (item.details as string) ?? (item.context as string) ?? '',
    updatedBy: (item.actorDisplayName as string) ?? (item.updatedBy as string),
  }));
  return {
    activity,
    serverTimeUtc: body.meta?.serverTimeUtc,
  };
};

// ── Paginated Activity Log ──

export const getIncidentActivityPage = async (
  incidentId: string,
  pageNumber: number,
  pageSize: number,
): Promise<IncidentActivityPage> => {
  const response = await apiClient.get(
    `/api/incidents/${encodeURIComponent(incidentId)}/activity?pageNumber=${pageNumber}&pageSize=${pageSize}`,
  );
  const body = response.data;

  // Normalize: API may return { data: { items, ... } } or { data: [...] } or { items: [...] }
  const dataPayload = body.data ?? body;
  let items: IncidentActivityItem[];
  let totalCount: number | undefined;
  let totalPages: number | undefined;

  if (Array.isArray(dataPayload)) {
    items = dataPayload;
  } else if (Array.isArray(dataPayload?.items)) {
    items = dataPayload.items;
    totalCount = typeof dataPayload.totalCount === 'number' ? dataPayload.totalCount : undefined;
    totalPages = typeof dataPayload.totalPages === 'number' ? dataPayload.totalPages : undefined;
  } else {
    items = [];
  }

  return {
    items,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    hasPreviousPage: pageNumber > 1,
    hasNextPage: totalPages != null ? pageNumber < totalPages : items.length === pageSize,
  };
};

// ── Headcount / Roster ──

type RosterParams = { viewId?: string; status?: string; search?: string; pageSize?: number; scope?: string };
type RosterPagination = { totalCount?: number; hasMore?: boolean; pageSize?: number };

const rosterLoadError = () => new Error('The incident roster response is incomplete. Please retry.');

const asObject = (value: unknown): Record<string, unknown> | undefined =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;

const extractRosterPeople = (body: Record<string, unknown>): IncidentRosterPerson[] => {
  const data = asObject(body.data);
  const candidates = [body.data, data?.people, data?.items, body.people];
  const people = candidates.find(Array.isArray) as IncidentRosterPerson[] | undefined;
  if (!people || people.some((person) => !asObject(person) || typeof person.personId !== 'string' || !person.personId.trim())) {
    throw rosterLoadError();
  }
  return people;
};

const optionalPaginationNumber = (value: unknown, min: number, max = Number.MAX_SAFE_INTEGER): number | undefined => {
  if (value == null) return undefined;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    throw new Error('The incident roster pagination is invalid. Please retry.');
  }
  return value;
};

const readRosterPagination = (body: Record<string, unknown>, page: number): RosterPagination => {
  const data = asObject(body.data);
  const meta = asObject(body.meta);
  const read = (key: string): unknown => data?.[key] ?? meta?.[key] ?? body[key];
  const responsePage = optionalPaginationNumber(read('pageNumber'), 1);
  if (responsePage != null && responsePage !== page) throw rosterLoadError();
  const hasMore = read('hasMore') ?? read('hasNextPage');
  if (hasMore != null && typeof hasMore !== 'boolean') throw rosterLoadError();
  return {
    totalCount: optionalPaginationNumber(read('totalCount'), 0),
    pageSize: optionalPaginationNumber(read('pageSize'), 1, 500),
    hasMore: hasMore as boolean | undefined,
  };
};

const reconcileRosterTotal = (previous?: number, next?: number): number | undefined => {
  if (previous != null && next != null && previous !== next) {
    throw new Error('The incident roster changed while loading. Please retry.');
  }
  return next ?? previous;
};

const hasMoreRosterPages = (pagination: RosterPagination, received: number, pageItems: number, pageSize: number): boolean => {
  if (pagination.totalCount != null) {
    if (received > pagination.totalCount) throw rosterLoadError();
    const more = received < pagination.totalCount;
    if (pagination.hasMore != null && pagination.hasMore !== more) throw rosterLoadError();
    if (more && pageItems === 0) throw rosterLoadError();
    return more;
  }
  if (pagination.hasMore === true && pageItems === 0) throw rosterLoadError();
  return pagination.hasMore ?? pageItems >= (pagination.pageSize ?? pageSize);
};

const ensureUniqueRosterPage = (people: IncidentRosterPerson[], seen: Set<string>): void => {
  for (const person of people) {
    if (seen.has(person.personId)) throw new Error('The incident roster returned a repeated page. Please retry.');
    seen.add(person.personId);
  }
};

const optionalRosterBoolean = (value: unknown): boolean | undefined => typeof value === 'boolean' ? value : undefined;

const normalizeRosterPerson = (person: IncidentRosterPerson): IncidentRosterPerson => ({
  ...person,
  status: normalizeApiPersonStatus(person.status),
  rosterSource: person.rosterSource ?? undefined,
  lastIncidentDayBadgeActivity: person.lastIncidentDayBadgeActivity ?? undefined,
  lobbyTrackActivity: person.lobbyTrackActivity ?? undefined,
  isAssignedToIncidentOffice: optionalRosterBoolean(person.isAssignedToIncidentOffice) ?? false,
  hasIncidentDayBadgeActivity: optionalRosterBoolean(person.hasIncidentDayBadgeActivity) ?? false,
  assignedOfficeLocationId: person.assignedOfficeLocationId ?? undefined,
  assignedOfficeLocationName: person.assignedOfficeLocationName ?? undefined,
  officeCubeNumber: person.officeCubeNumber ?? undefined,
  inclusionReasons: person.inclusionReasons ?? [],
  isInAccountabilityScope: optionalRosterBoolean(person.isInAccountabilityScope),
  isDefaultVisible: optionalRosterBoolean(person.isDefaultVisible),
  isAssignedOfficeNoBadgeToday: optionalRosterBoolean(person.isAssignedOfficeNoBadgeToday),
});

const rosterPageUrl = (incidentId: string, params: RosterParams | undefined, page: number, pageSize: number): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (key !== 'pageSize' && value) query.set(key, String(value));
  }
  query.set('pageSize', String(pageSize));
  query.set('pageNumber', String(page));
  return `/api/incidents/${encodeURIComponent(incidentId)}/roster?${query.toString()}`;
};

export const getIncidentRoster = async (
  incidentId: string,
  params?: RosterParams,
): Promise<{ people: IncidentRosterPerson[]; serverTimeUtc?: string }> => {
  const requestedPageSize = params?.pageSize ?? 500;
  const effectivePageSize = Number.isFinite(requestedPageSize)
    ? Math.min(500, Math.max(1, Math.floor(requestedPageSize)))
    : 500;
  const allPeople: IncidentRosterPerson[] = [];
  const seenPersonIds = new Set<string>();
  let serverTimeUtc: string | undefined;
  let expectedTotal: number | undefined;
  let page = 1;
  while (true) {
    const response = await apiClient.get<IncidentRosterResponse>(rosterPageUrl(incidentId, params, page, effectivePageSize));
    const body = asObject(response.data);
    if (!body || body.error) throw rosterLoadError();
    const people = extractRosterPeople(body);
    const pagination = readRosterPagination(body, page);
    expectedTotal = reconcileRosterTotal(expectedTotal, pagination.totalCount);
    ensureUniqueRosterPage(people, seenPersonIds);
    allPeople.push(...people.map(normalizeRosterPerson));
    serverTimeUtc = response.data.meta?.serverTimeUtc ?? serverTimeUtc;
    if (!hasMoreRosterPages({ ...pagination, totalCount: expectedTotal }, allPeople.length, people.length, effectivePageSize)) break;
    page++;
  }
  return { people: allPeople, serverTimeUtc };
};

export const submitStatusBatch = async (
  incidentId: string,
  updates: StatusUpdateInput[],
): Promise<{ responseData: StatusBatchResponseData | null; serverTimeUtc?: string }> => {
  const request: StatusBatchRequest = {
    clientBatchId: crypto.randomUUID(),
    clientSubmittedAtUtc: new Date().toISOString(),
    items: updates.map((u) => ({
      clientUpdateId: crypto.randomUUID(),
      personId: u.personId,
      status: toApiPersonStatus(u.status),
      observedAtUtc: u.observedAtUtc,
      ...(u.notes ? { notes: u.notes } : {}),
    })),
  };
  const response = await apiClient.post<StatusBatchResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/status-batches`,
    request,
  );
  return {
    responseData: response.data?.data ?? null,
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};

export const addUnlistedPerson = async (
  incidentId: string,
  request: AddUnlistedPersonRequest,
): Promise<{ person?: IncidentRosterPerson; serverTimeUtc?: string }> => {
  const response = await apiClient.post<AddUnlistedPersonResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/unlisted-persons`,
    request,
  );
  const body = response.data;
  const raw = body?.data as unknown as Record<string, unknown> | undefined;
  const person: IncidentRosterPerson | undefined = raw
    ? { ...(raw as unknown as IncidentRosterPerson), status: normalizeApiPersonStatus(raw.status) }
    : undefined;
  return {
    person,
    serverTimeUtc: body?.meta?.serverTimeUtc,
  };
};

export const getUnaccountedPeople = async (
  incidentId: string,
  params?: { status?: string; search?: string },
): Promise<{ notAccounted: IncidentRosterPerson[]; serverTimeUtc?: string }> => {
  const normalizePerson = (raw: Record<string, unknown>): IncidentRosterPerson => ({
    personId: raw.personId as string,
    displayName: raw.displayName as string,
    email: (raw.email as string) ?? undefined,
    phone: (raw.phone as string) ?? undefined,
    personType: (raw.personType != null ? String(raw.personType) : undefined),
    practice: (raw.practice as string) ?? undefined,
    floor: (raw.floor as string) ?? undefined,
    location: (raw.location as string) ?? undefined,
    companyName: (raw.companyName as string) ?? undefined,
    tenantName: (raw.tenantName as string) ?? undefined,
    rosterSource: (raw.rosterSource as string) ?? undefined,
    lastIncidentDayBadgeActivity: (raw.lastIncidentDayBadgeActivity as IncidentRosterPerson['lastIncidentDayBadgeActivity']) ?? undefined,
    lobbyTrackActivity: (raw.lobbyTrackActivity as IncidentRosterPerson['lobbyTrackActivity']) ?? undefined,
    isAssignedToIncidentOffice: typeof raw.isAssignedToIncidentOffice === 'boolean' ? raw.isAssignedToIncidentOffice : false,
    hasIncidentDayBadgeActivity: typeof raw.hasIncidentDayBadgeActivity === 'boolean' ? raw.hasIncidentDayBadgeActivity : false,
    assignedOfficeLocationId: (raw.assignedOfficeLocationId as string) ?? undefined,
    assignedOfficeLocationName: (raw.assignedOfficeLocationName as string) ?? undefined,
    officeCubeNumber: (raw.officeCubeNumber as string) ?? undefined,
    inclusionReasons: (raw.inclusionReasons as IncidentRosterPerson['inclusionReasons']) ?? [],
    lastBadgeAtUtc: (raw.lastBadgeAtUtc as string) ?? undefined,
    lastBadgeLocation: (raw.lastBadgeLocation as string) ?? undefined,
    lastBadgeReaderName: (raw.lastBadgeReaderName as string) ?? undefined,
    lastBadgeDoorName: (raw.lastBadgeDoorName as string) ?? undefined,
    lobbyTrackCheckInAtUtc: (raw.lobbyTrackCheckInAtUtc as string) ?? undefined,
    hasBadgeActivityToday: typeof raw.hasBadgeActivityToday === 'boolean' ? raw.hasBadgeActivityToday : undefined,
    status: normalizeApiPersonStatus(raw.status),
    lastUpdatedAtUtc: (raw.lastUpdatedAtUtc as string) ?? undefined,
    lastUpdatedByName: (raw.lastUpdatedByDisplayName as string) ?? (raw.lastUpdatedByName as string) ?? undefined,
    isUnlisted: (raw.isUnlisted as boolean) ?? undefined,
  });

  const extractItems = (body: UnaccountedResponse): Record<string, unknown>[] => {
    const dataObj = body.data && !Array.isArray(body.data) ? body.data : null;
    const src = dataObj ?? body;
    if (Array.isArray(src)) return src as Record<string, unknown>[];
    if (src && typeof src === 'object') {
      const s = src as Record<string, unknown>;
      if (Array.isArray(s.items)) return s.items as Record<string, unknown>[];
      if (Array.isArray(s.people)) return s.people as Record<string, unknown>[];
    }
    return [];
  };

  const baseUrl = `/api/incidents/${encodeURIComponent(incidentId)}/unaccounted`;
  const searchParam = params?.search ? `&search=${encodeURIComponent(params.search)}` : '';
  const statusParam = params?.status ? `status=${encodeURIComponent(params.status)}` : 'status=notAccounted';

  const res = await apiClient.get<UnaccountedResponse>(`${baseUrl}?${statusParam}${searchParam}`);

  const notAccounted = extractItems(res.data).map((p) => normalizePerson(p));

  return {
    notAccounted,
    serverTimeUtc: res.data?.meta?.serverTimeUtc,
  };
};

export const createCheckpoint = async (
  incidentId: string,
  request: CreateCheckpointRequest,
): Promise<{ serverTimeUtc?: string }> => {
  const response = await apiClient.post<CreateCheckpointResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/checkpoints`,
    request,
  );
  return {
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};

export const closeIncident = async (
  incidentId: string,
  request: CloseIncidentRequest,
): Promise<{ serverTimeUtc?: string }> => {
  const payload = {
    ...request,
    ...(request.incidentType != null ? { incidentType: incidentTypeApiCode(request.incidentType) } : {}),
  };
  const response = await apiClient.post<CloseIncidentResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/close`,
    payload,
  ).catch((error: unknown) => {
    const response = (error as AxiosError<{ error?: { code?: string } }>)?.response;
    const code = response?.data?.error?.code;
    if (response?.status === 400 && code === 'incident_type_required') {
      throw new Error('Select an incident type before closing this incident.');
    }
    if (response?.status === 400 && code === 'invalid_incident_type') {
      throw new Error('Select a valid incident type before closing this incident.');
    }
    if (response?.status === 409 && code === 'incident_type_conflict') {
      throw new Error('The incident type changed. Refresh the incident and try closing it again.');
    }
    throw error;
  });
  return {
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};

export const getIncidentAssignments = async (
  incidentId: string,
): Promise<IncidentAssignmentsResponse> => {
  const response = await apiClient.get<IncidentAssignmentsResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/assignments`,
  );
  const body = response.data;
  // Normalize badge/roster fields on each assignment
  if (body.data?.assignments) {
    body.data.assignments = body.data.assignments.map((a) => {
      const raw = a as unknown as Record<string, unknown>;
      return {
        ...a,
        status: raw.status != null ? normalizeApiPersonStatus(raw.status) : undefined,
        rosterSource: (raw.rosterSource as string) ?? undefined,
        lastIncidentDayBadgeActivity: (raw.lastIncidentDayBadgeActivity as typeof a.lastIncidentDayBadgeActivity) ?? undefined,
        lobbyTrackActivity: (raw.lobbyTrackActivity as typeof a.lobbyTrackActivity) ?? undefined,
        isAssignedToIncidentOffice: typeof raw.isAssignedToIncidentOffice === 'boolean' ? raw.isAssignedToIncidentOffice : undefined,
        hasIncidentDayBadgeActivity: typeof raw.hasIncidentDayBadgeActivity === 'boolean' ? raw.hasIncidentDayBadgeActivity : undefined,
        inclusionReasons: (raw.inclusionReasons as typeof a.inclusionReasons) ?? undefined,
        isUnlisted: typeof raw.isUnlisted === 'boolean' ? raw.isUnlisted : undefined,
        isAssignedOfficeNoBadgeToday: typeof raw.isAssignedOfficeNoBadgeToday === 'boolean' ? raw.isAssignedOfficeNoBadgeToday : undefined,
      };
    });
  }
  return body;
};

// ── Edit Incident Details ──

export const updateIncidentDetails = async (
  incidentId: string,
  request: UpdateIncidentDetailsRequest,
): Promise<{ incident?: Incident; serverTimeUtc?: string }> => {
  const payload = {
    ...request,
    ...(request.incidentType != null
      ? { incidentType: incidentTypeApiCode(request.incidentType) }
      : {}),
  };
  const response = await apiClient.patch<UpdateIncidentDetailsResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/details`,
    payload,
  );
  return {
    incident: response.data?.data ? {
      ...response.data.data,
      incidentType: normalizeIncidentType(response.data.data.incidentType),
      status: normalizeIncidentStatus(response.data.data.status),
    } : undefined,
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};
